import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import type { Tender } from '../types/tender';
import { firestore } from './firebase';

export const getPlatformCostForTender = async (
  clientId: string,
  tender: Tender
): Promise<number> => {
  if (!tender?.id || !clientId) {
    return 0;
  }

  try {
    const pricingDoc = await getDoc(doc(firestore, 'platformSettings', 'pricing'));
    const costPerCredit = pricingDoc.data()?.cost_per_credit_in_base || 1.0;

    let totalCredits = 0;

    // Fetch from transactions
    const txQuery1 = query(
      collection(firestore, 'clients', clientId, 'billingLedger', 'main', 'transactions'),
      where('tender_id', '==', tender.id)
    );
    const txQuery2 = query(
      collection(firestore, 'clients', clientId, 'billingLedger', 'main', 'transactions'),
      where('tenderId', '==', tender.id)
    );
    const txQuery3 = query(
      collection(firestore, 'clients', clientId, 'billingLedger', 'main', 'transactions'),
      where('tender_id', '==', tender.tenderId || '')
    );
    const txQuery4 = query(
      collection(firestore, 'clients', clientId, 'billingLedger', 'main', 'transactions'),
      where('tenderId', '==', tender.tenderId || '')
    );
    const txQuery5 = query(
      collection(firestore, 'clients', clientId, 'billingLedger', 'main', 'transactions'),
      where('tender_id', '==', tender.sourceStoreTenderId || '')
    );

    // Fetch from jobs
    const jobsQuery1 = query(
      collection(firestore, 'clients', clientId, 'billingLedger', 'main', 'jobs'),
      where('tender_id', '==', tender.id)
    );
    const jobsQuery2 = query(
      collection(firestore, 'clients', clientId, 'billingLedger', 'main', 'jobs'),
      where('tenderId', '==', tender.id)
    );
    const jobsQuery3 = query(
      collection(firestore, 'clients', clientId, 'billingLedger', 'main', 'jobs'),
      where('tender_id', '==', tender.tenderId || '')
    );
    const jobsQuery4 = query(
      collection(firestore, 'clients', clientId, 'billingLedger', 'main', 'jobs'),
      where('tenderId', '==', tender.tenderId || '')
    );
    const jobsQuery5 = query(
      collection(firestore, 'clients', clientId, 'billingLedger', 'main', 'jobs'),
      where('tender_id', '==', tender.sourceStoreTenderId || '')
    );

    const [
      txSnap1,
      txSnap2,
      txSnap3,
      txSnap4,
      txSnap5,
      jobsSnap1,
      jobsSnap2,
      jobsSnap3,
      jobsSnap4,
      jobsSnap5,
    ] = await Promise.all([
      getDocs(txQuery1),
      getDocs(txQuery2),
      tender.tenderId ? getDocs(txQuery3) : Promise.resolve({ docs: [] }),
      tender.tenderId ? getDocs(txQuery4) : Promise.resolve({ docs: [] }),
      tender.sourceStoreTenderId ? getDocs(txQuery5) : Promise.resolve({ docs: [] }),
      getDocs(jobsQuery1),
      getDocs(jobsQuery2),
      tender.tenderId ? getDocs(jobsQuery3) : Promise.resolve({ docs: [] }),
      tender.tenderId ? getDocs(jobsQuery4) : Promise.resolve({ docs: [] }),
      tender.sourceStoreTenderId ? getDocs(jobsQuery5) : Promise.resolve({ docs: [] }),
    ]);

    const processedDocIds = new Set<string>();

    for (const d of [
      ...txSnap1.docs,
      ...txSnap2.docs,
      ...txSnap3.docs,
      ...txSnap4.docs,
      ...txSnap5.docs,
    ] as {
      id: string;
      data: () => { total_credits_burned?: number; credits_burned?: number };
    }[]) {
      if (!processedDocIds.has(d.id)) {
        totalCredits += (d.data().total_credits_burned ?? d.data().credits_burned) || 0;
        processedDocIds.add(d.id);
      }
    }

    for (const d of [
      ...jobsSnap1.docs,
      ...jobsSnap2.docs,
      ...jobsSnap3.docs,
      ...jobsSnap4.docs,
      ...jobsSnap5.docs,
    ] as {
      id: string;
      data: () => { total_credits_burned?: number; credits_burned?: number };
    }[]) {
      if (!processedDocIds.has(d.id)) {
        totalCredits += (d.data().total_credits_burned ?? d.data().credits_burned) || 0;
        processedDocIds.add(d.id);
      }
    }

    return totalCredits * costPerCredit;
  } catch (_error) {
    return 0;
  }
};

export interface BillingLedger {
  balance: number;
  accumulated_usage: number;
  isPAYG?: boolean;
  discount?: number;
  discount_expires_at?: unknown;
  fallback_discount?: number;
  spending_limit?: number;
  current_cycle_costs_by_function?: Record<string, number>;
  current_cycle_costs_by_team?: Record<string, number>;
  current_cycle_costs_by_user?: Record<string, number>;
}

export const subscribeToBillingLedger = (
  clientId: string,
  callback: (ledger: BillingLedger | null) => void
) => {
  const ledgerRef = doc(firestore, 'clients', clientId, 'billingLedger', 'main');
  return onSnapshot(
    ledgerRef,
    (snap) => {
      if (snap.exists()) {
        callback(snap.data() as BillingLedger);
      } else {
        callback(null);
      }
    },
    () => {
      // Ignore snapshot stream errors on sign-out / token expiry
    }
  );
};

export const getBillingLedger = async (clientId: string): Promise<BillingLedger | null> => {
  const snap = await getDoc(doc(firestore, 'clients', clientId, 'billingLedger', 'main'));
  if (snap.exists()) {
    return snap.data() as BillingLedger;
  }
  return null;
};

export const updateBillingLedger = async (clientId: string, updates: Partial<BillingLedger>) => {
  const ledgerRef = doc(firestore, 'clients', clientId, 'billingLedger', 'main');
  await updateDoc(ledgerRef, { ...updates, updatedAt: serverTimestamp() });
};

export const topUpBillingLedger = async (
  clientId: string,
  amount: number,
  currentBalance: number,
  userId: string
) => {
  const ledgerRef = doc(firestore, 'clients', clientId, 'billingLedger', 'main');
  const newBalance = currentBalance + amount;
  await setDoc(ledgerRef, { balance: newBalance, updatedAt: serverTimestamp() }, { merge: true });

  const txsRef = collection(ledgerRef, 'jobs');
  await addDoc(txsRef, {
    action_type: 'manual_topup',
    total_credits_burned: 0,
    credits_added: amount,
    previous_balance: currentBalance,
    new_balance: newBalance,
    timestamp: serverTimestamp(),
    updatedAt: serverTimestamp(),
    user_id: userId,
  });
};

export const getInvoices = async (clientId: string, limitCount = 12) => {
  const q = query(
    collection(firestore, 'clients', clientId, 'invoices'),
    orderBy('period_end', 'desc'),
    limit(limitCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const getBillingJobs = async (clientId: string, limitCount = 10000) => {
  const q = query(
    collection(firestore, 'clients', clientId, 'billingLedger', 'main', 'jobs'),
    limit(limitCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const getBillingTransactions = async (clientId: string, limitCount = 10000) => {
  const q = query(
    collection(firestore, 'clients', clientId, 'billingLedger', 'main', 'transactions'),
    orderBy('timestamp', 'desc'),
    limit(limitCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};
